import React, { useEffect, useRef, useState } from "react";
import { Pressable, ScrollView, Text, View } from "react-native";
import { CalendarDays, Check } from "lucide-react-native";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import {
  DateField,
  Dialog,
  PrimaryButton,
  SecondaryButton,
  TextField,
  type TextFieldProps,
} from "@louise/moon-ui";
import { MoonPhoneField } from "@louise/moon-ui/primitives/MoonPhoneField";
import {
  AddressField,
  type AddressData,
} from "@louise/moon-ui/primitives/AddressField";
import { EMBER } from "../../../onboarding/theme";
import { getApiErrorMessage } from "@src/packages/api/api-error";
import type { ConciergePrescriptionFile } from "./PharmacyPrescriptionSection";

const BROWN = "#6B5548";
const BROWN_MUTED = "rgba(107, 85, 72, 0.55)";
const BROWN_LINE = "rgba(107, 85, 72, 0.18)";

// `MoonPhoneField` requires an E.164 value. Patient records may hold the raw
// digits; normalise once on prefill so the country picker resolves correctly.
function normalizeE164(raw: string): string {
  if (!raw) return "";
  const parsed = parsePhoneNumberFromString(raw, "US");
  return parsed?.isValid() ? parsed.number : raw;
}

// Compose the display string AddressField shows in its input, from the parts
// we persist separately. Mirrors what Google Places returns as formattedAddress.
function composeAddressDisplay(parts: {
  address1: string;
  city: string;
  zipCode: string;
}): string {
  const cityZip = [parts.city, parts.zipCode].filter(Boolean).join(" ").trim();
  return [parts.address1, cityZip].filter(Boolean).join(", ");
}

export type ConciergeQuoteFormValues = {
  firstName: string;
  lastName: string;
  /** ISO YYYY-MM-DD (as emitted by DateField). */
  dateOfBirth: string;
  email: string;
  /** E.164 (as emitted by MoonPhoneField), e.g. "+15551234567". */
  phone: string;
  addressLine1: string;
  city: string;
  /** State/region short code, e.g. the US 2-letter "NY". */
  state: string;
  zipCode: string;
  country: string;
  clinicName: string;
  prescriberName: string;
  anticipatedStartDate: string;
  cycleType: string;
  isMilitary: boolean;
  consented: boolean;
};

type Props = {
  visible: boolean;
  onClose: () => void;
  file: Pick<ConciergePrescriptionFile, "name"> | null;
  /**
   * Sends the request to the backend. The sheet stays open (with the error
   * under the consent checkbox) when this rejects, and resets + closes once
   * it resolves.
   */
  onSubmit: (values: ConciergeQuoteFormValues) => void | Promise<void>;

  // Pre-fill values (from patient profile or Rx extraction)
  initialFirstName?: string;
  initialLastName?: string;
  initialDateOfBirth?: string;
  initialEmail?: string;
  initialPhone?: string;
  initialAddressLine1?: string;
  initialCity?: string;
  initialZipCode?: string;
  initialCountry?: string;
  initialClinicName?: string;
  initialPrescriberName?: string;
  initialAnticipatedStartDate?: string;

  // Labels
  titleLabel: string;
  step1TitleLabel: string;
  step1BodyLabel: string;
  step2TitleLabel: string;
  step2BodyLabel: string;
  step3TitleLabel: string;
  step3BodyLabel: string;
  firstNameLabel: string;
  firstNamePlaceholder: string;
  lastNameLabel: string;
  lastNamePlaceholder: string;
  dobLabel: string;
  emailLabel: string;
  emailPlaceholder: string;
  phoneLabel: string;
  addressLabel: string;
  addressPlaceholder: string;
  clinicNameLabel: string;
  clinicNamePlaceholder: string;
  prescriberNameLabel: string;
  prescriberNamePlaceholder: string;
  cycleTypeLabel: string;
  cycleTypeOptions: { id: string; label: string }[];
  anticipatedStartLabel: string;
  militaryLabel: string;
  consentLabel: string;
  /** Shown when `onSubmit` rejects without a usable message. */
  submitFailedError: string;
  submitLabel: string;
  continueLabel: string;
  backLabel: string;
  closeLabel: string;
  /**
   * Prefix for the "which fields are missing" message shown when the user
   * presses Continue with an empty required field, e.g. "Missing:" — the
   * sheet appends the specific field labels so the user knows exactly what
   * to fix instead of "fill all fields".
   */
  missingFieldsPrefixLabel: string;
  step3RequiredError: string;
};

/**
 * Concierge quote request sheet, on the shared moon-ui Dialog (which handles
 * the keyboard, height and safe area), scoped to the Pharmacy screen. Three short steps: (1) about you, (2) clinic and
 * treatment, (3) review and consent. The prescription file itself comes from
 * the Prescription section, not re-uploaded here.
 */
export function PharmacyConciergeQuoteSheet({
  visible,
  onClose,
  file,
  onSubmit,
  initialFirstName,
  initialLastName,
  initialDateOfBirth,
  initialEmail,
  initialPhone,
  initialAddressLine1,
  initialCity,
  initialZipCode,
  initialCountry,
  initialClinicName,
  initialPrescriberName,
  initialAnticipatedStartDate,
  titleLabel,
  step1TitleLabel,
  step1BodyLabel,
  step2TitleLabel,
  step2BodyLabel,
  step3TitleLabel,
  step3BodyLabel,
  firstNameLabel,
  firstNamePlaceholder,
  lastNameLabel,
  lastNamePlaceholder,
  dobLabel,
  emailLabel,
  emailPlaceholder,
  phoneLabel,
  addressLabel,
  addressPlaceholder,
  clinicNameLabel,
  clinicNamePlaceholder,
  prescriberNameLabel,
  prescriberNamePlaceholder,
  cycleTypeLabel,
  cycleTypeOptions,
  anticipatedStartLabel,
  militaryLabel,
  consentLabel,
  submitFailedError,
  submitLabel,
  continueLabel,
  backLabel,
  closeLabel,
  missingFieldsPrefixLabel,
  step3RequiredError,
}: Props) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [firstName, setFirstName] = useState(initialFirstName ?? "");
  const [lastName, setLastName] = useState(initialLastName ?? "");
  const [dateOfBirth, setDateOfBirth] = useState(initialDateOfBirth ?? "");
  const [email, setEmail] = useState(initialEmail ?? "");
  const [phone, setPhone] = useState(() => normalizeE164(initialPhone ?? ""));
  const [addressLine1, setAddressLine1] = useState(initialAddressLine1 ?? "");
  const [city, setCity] = useState(initialCity ?? "");
  // Captured from the AddressField's structured payload; not prefilled since
  // the patient profile does not carry a separate state field.
  const [state, setState] = useState("");
  const [zipCode, setZipCode] = useState(initialZipCode ?? "");
  const [country, setCountry] = useState(initialCountry ?? "");
  const [clinicName, setClinicName] = useState(initialClinicName ?? "");
  const [prescriberName, setPrescriberName] = useState(
    initialPrescriberName ?? "",
  );
  const [cycleType, setCycleType] = useState<string>("");
  const [anticipatedStartDate, setAnticipatedStartDate] = useState(
    initialAnticipatedStartDate ?? "",
  );
  const [isMilitary, setIsMilitary] = useState(false);
  const [consented, setConsented] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showStep1Error, setShowStep1Error] = useState(false);
  const [showStep2Error, setShowStep2Error] = useState(false);
  const [showStep3Error, setShowStep3Error] = useState(false);

  // Re-seed inputs each time the sheet opens so freshly-extracted values from
  // the Rx (or profile changes) are surfaced. User edits within a single open
  // session are preserved until the sheet is closed and reopened.
  const initialsRef = useRef({
    firstName: initialFirstName ?? "",
    lastName: initialLastName ?? "",
    dateOfBirth: initialDateOfBirth ?? "",
    email: initialEmail ?? "",
    phone: normalizeE164(initialPhone ?? ""),
    addressLine1: initialAddressLine1 ?? "",
    city: initialCity ?? "",
    zipCode: initialZipCode ?? "",
    country: initialCountry ?? "",
    clinicName: initialClinicName ?? "",
    prescriberName: initialPrescriberName ?? "",
    anticipatedStartDate: initialAnticipatedStartDate ?? "",
  });
  initialsRef.current = {
    firstName: initialFirstName ?? "",
    lastName: initialLastName ?? "",
    dateOfBirth: initialDateOfBirth ?? "",
    email: initialEmail ?? "",
    phone: normalizeE164(initialPhone ?? ""),
    addressLine1: initialAddressLine1 ?? "",
    city: initialCity ?? "",
    zipCode: initialZipCode ?? "",
    country: initialCountry ?? "",
    clinicName: initialClinicName ?? "",
    prescriberName: initialPrescriberName ?? "",
    anticipatedStartDate: initialAnticipatedStartDate ?? "",
  };
  const previouslyVisibleRef = useRef(visible);
  useEffect(() => {
    const wasVisible = previouslyVisibleRef.current;
    previouslyVisibleRef.current = visible;
    if (visible && !wasVisible) {
      setStep(1);
      setFirstName(initialsRef.current.firstName);
      setLastName(initialsRef.current.lastName);
      setDateOfBirth(initialsRef.current.dateOfBirth);
      setEmail(initialsRef.current.email);
      setPhone(initialsRef.current.phone);
      setAddressLine1(initialsRef.current.addressLine1);
      setCity(initialsRef.current.city);
      setState("");
      setZipCode(initialsRef.current.zipCode);
      setCountry(initialsRef.current.country);
      setClinicName(initialsRef.current.clinicName);
      setPrescriberName(initialsRef.current.prescriberName);
      setAnticipatedStartDate(initialsRef.current.anticipatedStartDate);
      setShowStep1Error(false);
      setShowStep2Error(false);
      setShowStep3Error(false);
      setSubmitError(null);
    }
  }, [visible]);

  const resetAndClose = () => {
    setStep(1);
    setFirstName("");
    setLastName("");
    setDateOfBirth("");
    setEmail("");
    setPhone("");
    setAddressLine1("");
    setCity("");
    setState("");
    setZipCode("");
    setCountry("");
    setClinicName("");
    setPrescriberName("");
    setCycleType("");
    setAnticipatedStartDate("");
    setIsMilitary(false);
    setConsented(false);
    setShowStep1Error(false);
    setShowStep2Error(false);
    setShowStep3Error(false);
    setSubmitError(null);
    onClose();
  };

  const missingStep1Fields = [
    { label: firstNameLabel, filled: firstName.trim().length > 0 },
    { label: lastNameLabel, filled: lastName.trim().length > 0 },
    { label: dobLabel, filled: dateOfBirth.trim().length > 0 },
    { label: emailLabel, filled: email.trim().length > 0 },
    { label: phoneLabel, filled: phone.trim().length > 0 },
    { label: addressLabel, filled: addressLine1.trim().length > 0 },
  ]
    .filter((f) => !f.filled)
    .map((f) => f.label);
  const step1Complete = missingStep1Fields.length === 0;

  const missingStep2Fields = [
    { label: clinicNameLabel, filled: clinicName.trim().length > 0 },
    { label: prescriberNameLabel, filled: prescriberName.trim().length > 0 },
    { label: cycleTypeLabel, filled: cycleType.length > 0 },
    {
      label: anticipatedStartLabel,
      filled: anticipatedStartDate.trim().length > 0,
    },
  ]
    .filter((f) => !f.filled)
    .map((f) => f.label);
  const step2Complete = missingStep2Fields.length === 0;

  const handleContinueFromStep1 = () => {
    if (!step1Complete) {
      setShowStep1Error(true);
      return;
    }
    setShowStep1Error(false);
    setStep(2);
  };

  const handleContinueFromStep2 = () => {
    if (!step2Complete) {
      setShowStep2Error(true);
      return;
    }
    setShowStep2Error(false);
    setStep(3);
  };

  const handleSubmit = async () => {
    if (!consented || submitting) {
      if (!consented) setShowStep3Error(true);
      return;
    }
    setShowStep3Error(false);
    setSubmitError(null);
    setSubmitting(true);
    try {
      await onSubmit({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        dateOfBirth: dateOfBirth.trim(),
        email: email.trim(),
        phone: phone.trim(),
        addressLine1: addressLine1.trim(),
        city: city.trim(),
        state: state.trim(),
        zipCode: zipCode.trim(),
        country: country.trim(),
        clinicName: clinicName.trim(),
        prescriberName: prescriberName.trim(),
        anticipatedStartDate: anticipatedStartDate.trim(),
        cycleType,
        isMilitary,
        consented,
      });
    } catch (error) {
      setSubmitError(getApiErrorMessage(error, submitFailedError));
      return;
    } finally {
      setSubmitting(false);
    }
    resetAndClose();
  };

  // Snapshot the initial address display *once* per mount. AddressField
  // manages its own on-screen query state after mount and — crucially — its
  // debounced search skips when the reactive `value` prop matches the current
  // typed query. Passing a value that re-composes on every keystroke created
  // that feedback loop and killed the autocomplete dropdown after the first
  // few characters. Freezing this to the mount-time value avoids the loop
  // while still letting the field prefill for returning patients.
  // Only prefill the visible address display when we actually have a street
  // address on file. A partial profile with only a city or postal code would
  // otherwise render "90210" in the field while `addressLine1` state stays
  // empty — the user thinks the field is filled but validation fails with
  // "Missing: Address". Show empty instead so the user knows to type it.
  const [addressInitialValue] = useState(() =>
    initialAddressLine1 && initialAddressLine1.trim().length > 0
      ? composeAddressDisplay({
          address1: initialAddressLine1,
          city: initialCity ?? "",
          zipCode: initialZipCode ?? "",
        })
      : "",
  );

  const handleAddressChange = (data: AddressData) => {
    setAddressLine1(data.address1 ?? "");
    setCity(data.city ?? "");
    setState(data.state ?? "");
    setZipCode(data.postalCode ?? "");
    setCountry(data.country ?? "");
  };

  return (
    <Dialog
      visible={visible}
      onClose={resetAndClose}
      layout="centered"
      size="wide"
      closeButton="minimal"
      closeAccessibilityLabel={closeLabel}
      closeAction="close_concierge_quote"
      backdropPressLabel="dismiss_concierge_quote_backdrop"
    >
      <ScrollView
        style={{ flexShrink: 1 }}
        showsVerticalScrollIndicator={true}
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={{ paddingBottom: 8 }}
      >
        <Text
          className="font-dm-sans"
          style={{
            color: BROWN,
            fontSize: 11,
            fontWeight: "800",
            letterSpacing: 1.2,
            textTransform: "uppercase",
          }}
        >
          {titleLabel}
        </Text>

        {file ? (
          <Text
            className="font-dm-sans"
            style={{
              color: BROWN_MUTED,
              fontSize: 12,
              marginTop: 6,
            }}
            numberOfLines={1}
          >
            {file.name}
          </Text>
        ) : null}

        <View style={{ marginTop: 16 }}>
          <StepDots current={step - 1} total={3} />
        </View>

        <View style={{ display: step === 1 ? "flex" : "none" }}>
          <Text
            className="font-dm-sans"
            style={{
              color: BROWN,
              fontSize: 20,
              fontWeight: "700",
            }}
          >
            {step1TitleLabel}
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              color: BROWN_MUTED,
              fontSize: 13,
              lineHeight: 19,
              marginTop: 8,
            }}
          >
            {step1BodyLabel}
          </Text>

          <Field
            label={firstNameLabel}
            value={firstName}
            onChangeText={setFirstName}
            placeholder={firstNamePlaceholder}
            autoCapitalize="words"
          />
          <Field
            label={lastNameLabel}
            value={lastName}
            onChangeText={setLastName}
            placeholder={lastNamePlaceholder}
            autoCapitalize="words"
          />
          <View style={{ marginTop: 14 }}>
            <DateField
              label={dobLabel}
              labelIcon={CalendarDays}
              value={dateOfBirth}
              onChange={setDateOfBirth}
            />
          </View>
          <Field
            label={emailLabel}
            value={email}
            onChangeText={setEmail}
            placeholder={emailPlaceholder}
            autoCapitalize="none"
            keyboardType="email-address"
          />
          <View style={{ marginTop: 14 }}>
            <MoonPhoneField
              label={phoneLabel}
              value={phone}
              onChange={setPhone}
              defaultCountry="US"
            />
          </View>
          <View style={{ marginTop: 14 }}>
            <AddressField
              label={addressLabel}
              value={addressInitialValue}
              onChange={handleAddressChange}
              placeholder={addressPlaceholder}
            />
          </View>

          {showStep1Error && !step1Complete ? (
            <StepValidationError
              message={`${missingFieldsPrefixLabel} ${missingStep1Fields.join(", ")}`}
            />
          ) : null}

          <View
            style={{
              flexDirection: "row",
              justifyContent: "flex-end",
              marginTop: 20,
            }}
          >
            <PrimaryButton
              label={continueLabel}
              onPress={handleContinueFromStep1}
              flex={1}
            />
          </View>
        </View>

        <View style={{ display: step === 2 ? "flex" : "none" }}>
          <Text
            className="font-dm-sans"
            style={{
              color: BROWN,
              fontSize: 20,
              fontWeight: "700",
            }}
          >
            {step2TitleLabel}
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              color: BROWN_MUTED,
              fontSize: 13,
              lineHeight: 19,
              marginTop: 8,
            }}
          >
            {step2BodyLabel}
          </Text>

          <Field
            label={clinicNameLabel}
            value={clinicName}
            onChangeText={setClinicName}
            placeholder={clinicNamePlaceholder}
            autoCapitalize="words"
          />
          <Field
            label={prescriberNameLabel}
            value={prescriberName}
            onChangeText={setPrescriberName}
            placeholder={prescriberNamePlaceholder}
            autoCapitalize="words"
          />

          <View style={{ marginTop: 14 }}>
            <Text
              className="font-dm-sans"
              style={{
                color: BROWN,
                fontSize: 12,
                fontWeight: "700",
                marginBottom: 8,
              }}
            >
              {cycleTypeLabel}
            </Text>
            <View
              style={{
                flexDirection: "row",
                flexWrap: "wrap",
                gap: 6,
              }}
            >
              {cycleTypeOptions.map((opt) => {
                const selected = cycleType === opt.id;
                return (
                  <Pressable
                    key={opt.id}
                    onPress={() => setCycleType(opt.id)}
                    accessibilityRole="radio"
                    accessibilityState={{ selected }}
                    style={{
                      paddingHorizontal: 12,
                      paddingVertical: 6,
                      borderRadius: 8,
                      borderWidth: 1,
                      borderColor: selected
                        ? EMBER.colors.warm
                        : EMBER.colors.border,
                      backgroundColor: selected
                        ? "rgba(139, 58, 26, 0.08)"
                        : "transparent",
                    }}
                  >
                    <Text
                      className="font-dm-sans"
                      style={{
                        fontSize: 12,
                        color: selected
                          ? EMBER.colors.warm
                          : EMBER.colors.textMid,
                        fontWeight: selected ? "600" : "400",
                      }}
                    >
                      {opt.label}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          </View>

          <View style={{ marginTop: 14 }}>
            <DateField
              label={anticipatedStartLabel}
              labelIcon={CalendarDays}
              value={anticipatedStartDate}
              onChange={setAnticipatedStartDate}
            />
          </View>

          <InlineCheckbox
            checked={isMilitary}
            onToggle={() => setIsMilitary((v) => !v)}
            label={militaryLabel}
          />

          {showStep2Error && !step2Complete ? (
            <StepValidationError
              message={`${missingFieldsPrefixLabel} ${missingStep2Fields.join(", ")}`}
            />
          ) : null}

          <StepFooter
            onBack={() => setStep(1)}
            backLabel={backLabel}
            onContinue={handleContinueFromStep2}
            continueLabel={continueLabel}
          />
        </View>

        <View style={{ display: step === 3 ? "flex" : "none" }}>
          <Text
            className="font-dm-sans"
            style={{
              color: BROWN,
              fontSize: 20,
              fontWeight: "700",
            }}
          >
            {step3TitleLabel}
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              color: BROWN_MUTED,
              fontSize: 13,
              lineHeight: 19,
              marginTop: 8,
            }}
          >
            {step3BodyLabel}
          </Text>

          <View style={{ marginTop: 18 }}>
            <InlineCheckbox
              checked={consented}
              onToggle={() => setConsented((v) => !v)}
              label={consentLabel}
              alignItems="flex-start"
            />
          </View>

          {showStep3Error && !consented ? (
            <StepValidationError message={step3RequiredError} />
          ) : null}
          {submitError ? <StepValidationError message={submitError} /> : null}

          <View
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: 20,
            }}
          >
            <SecondaryButton
              label={backLabel}
              action="access.pharmacy.concierge_quote_back"
              onPress={() => setStep(2)}
              flex={1}
            />
            <PrimaryButton
              label={submitLabel}
              onPress={() => void handleSubmit()}
              disabled={!consented || submitting}
              flex={1}
            />
          </View>
        </View>
      </ScrollView>
    </Dialog>
  );
}

// The shared moon-ui TextField: label, box and input chrome identical to the
// rest of the app, so the concierge sheet reads as part of the same app.
function Field({
  label,
  value,
  onChangeText,
  placeholder,
  autoCapitalize,
  keyboardType,
  autoComplete,
  maxLength,
}: {
  label: string;
  value: string;
  onChangeText: (v: string) => void;
  placeholder: string;
  autoCapitalize?: TextFieldProps["autoCapitalize"];
  keyboardType?: TextFieldProps["keyboardType"];
  autoComplete?: TextFieldProps["autoComplete"];
  maxLength?: number;
}) {
  return (
    <View style={{ marginTop: 14 }}>
      <TextField
        label={label}
        value={value}
        onChangeText={onChangeText}
        placeholder={placeholder}
        autoCapitalize={autoCapitalize}
        keyboardType={keyboardType ?? "default"}
        autoComplete={autoComplete}
        maxLength={maxLength}
        returnKeyType="done"
      />
    </View>
  );
}

// Plain inline checkbox: a compact square + label, no card background. The
// grey-wash background variant we used previously wasn't in the app anywhere
// else, so this reads as native rather than bespoke.
function InlineCheckbox({
  checked,
  onToggle,
  label,
  alignItems = "center",
}: {
  checked: boolean;
  onToggle: () => void;
  label: string;
  alignItems?: "center" | "flex-start";
}) {
  return (
    <Pressable
      onPress={onToggle}
      accessibilityRole="checkbox"
      accessibilityState={{ checked }}
      accessibilityLabel={label}
      style={{
        flexDirection: "row",
        alignItems,
        gap: 10,
        paddingVertical: 4,
      }}
    >
      <View
        style={{
          width: 20,
          height: 20,
          borderRadius: 6,
          borderWidth: 1.5,
          borderColor: BROWN,
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: checked ? BROWN : "transparent",
          marginTop: alignItems === "flex-start" ? 2 : 0,
        }}
      >
        {checked ? <Check size={12} color="#FFFFFF" strokeWidth={3} /> : null}
      </View>
      <Text
        className="font-dm-sans"
        style={{
          color: BROWN,
          fontSize: 13,
          flex: 1,
          lineHeight: 19,
        }}
      >
        {label}
      </Text>
    </Pressable>
  );
}

// Matches AddAppointmentModal's boxed error style so validation messaging
// stays consistent across the app instead of the plain coral text that was
// unique to this sheet.
function StepValidationError({ message }: { message: string }) {
  return (
    <View
      style={{
        marginTop: 12,
        backgroundColor: "rgba(220, 38, 38, 0.08)",
        borderRadius: 12,
        borderWidth: 1,
        borderColor: "rgba(220, 38, 38, 0.2)",
        padding: 12,
      }}
    >
      <Text
        className="font-dm-sans"
        style={{ fontSize: 13, color: "#DC2626" }}
        accessibilityRole="alert"
      >
        {message}
      </Text>
    </View>
  );
}

function StepDots({ current, total }: { current: number; total: number }) {
  return (
    <View
      style={{
        flexDirection: "row",
        justifyContent: "center",
        gap: 6,
        marginBottom: 16,
      }}
    >
      {Array.from({ length: total }).map((_, i) => (
        <View
          key={i}
          style={{
            width: i === current ? 18 : 6,
            height: 6,
            borderRadius: 3,
            backgroundColor:
              i === current
                ? EMBER.colors.warm
                : i < current
                  ? "rgba(184, 74, 22, 0.35)"
                  : "rgba(0, 0, 0, 0.06)",
          }}
        />
      ))}
    </View>
  );
}

function StepFooter({
  onBack,
  backLabel,
  onContinue,
  continueLabel,
}: {
  onBack: () => void;
  backLabel: string;
  onContinue: () => void;
  continueLabel: string;
}) {
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
        marginTop: 20,
      }}
    >
      <SecondaryButton
        label={backLabel}
        action="access.pharmacy.concierge_quote_back"
        onPress={onBack}
        flex={1}
      />
      <PrimaryButton label={continueLabel} onPress={onContinue} flex={1} />
    </View>
  );
}
